import { createReducer, on } from '@ngrx/store';
import {
  IContract,
  IMasterDataUser,
  IResidentS3File,
  ContractStatus,
  IMasterdataChangeConfiguration,
  IMasterDataChange,
  IMasterdataChangeRequestResponseBean,
} from '../../../models';
import { ActionStateCreator, IActionState, sortContracts } from '../../../utils';
import * as fromActions from './masterdata.actions';

const getPrimaryContract = (user: IMasterDataUser, contracts?: IContract[]): IContract => {
  let contract: IContract;
  if (contracts?.length === 1) {
    contract = contracts[0];
    return contract;
  } else if (contracts?.length > 1) {
    if (user?.mainContractId) {
      contract = contracts.find(el => el.externalId === user?.mainContractId);
    } else if (contracts.some(el => el.status !== ContractStatus.EXPIRED && el.type === 'FLAT')) {
      contract = contracts.find(el => el.status !== ContractStatus.EXPIRED && el.type === 'FLAT');
    } else if (contracts.some(el => el.status !== ContractStatus.EXPIRED)) {
      contract = contracts.find(el => el.status !== ContractStatus.EXPIRED);
    } else {
      contract = contracts[0];
    }
    return contract;
  }
  return null;
};

export interface IMasterDataState {
  user: IMasterDataUser;
  userActionState: IActionState;
  updateMasterDataActionState: IActionState;
  contracts: IContract[];
  contractsActionState: IActionState;
  selectedContract: IContract;
  selectedContractDocumentsActionState: IActionState;
  selectedContractDocuments: IResidentS3File[];
  selectedContractSelectedDocument: IResidentS3File;
  masterDataChangeConfiguration: IMasterdataChangeConfiguration[] | null;
  masterDataChangeConfigurationActionState: IActionState;
  masterDataChange: IMasterDataChange;
  masterDataChangeActionState: IActionState;
  sendMasterDataChangeRequestResponse: IMasterdataChangeRequestResponseBean;
  sendMasterdataChangeRequestActionState: IActionState;
  sendMasterDataChangeTicketResponse: boolean;
  sendMasterdataChangeTicketActionState: IActionState;
}

export const initialState: IMasterDataState = {
  user: null,
  userActionState: ActionStateCreator.create(),
  updateMasterDataActionState: ActionStateCreator.create(),
  contracts: [],
  contractsActionState: ActionStateCreator.create(),
  selectedContract: null,
  selectedContractDocumentsActionState: ActionStateCreator.create(),
  selectedContractDocuments: [],
  selectedContractSelectedDocument: null,
  masterDataChangeConfiguration: null,
  masterDataChangeConfigurationActionState: ActionStateCreator.create(),
  masterDataChange: null,
  masterDataChangeActionState: ActionStateCreator.create(),
  sendMasterDataChangeRequestResponse: null,
  sendMasterdataChangeRequestActionState: ActionStateCreator.create(),
  sendMasterDataChangeTicketResponse: null,
  sendMasterdataChangeTicketActionState: ActionStateCreator.create(),
};

export const masterDataReducer = createReducer(
  initialState,

  on(fromActions.LoadMasterData, state => {
    return {
      ...state,
      userActionState: ActionStateCreator.onStart(),
    };
  }),

  on(fromActions.LoadMasterDataSuccess, (state, { user }) => {
    return {
      ...state,
      userActionState: ActionStateCreator.onSuccess(),
      user,
      selectedContract: getPrimaryContract(user, state.contracts),
    };
  }),

  on(fromActions.LoadMasterDataFailed, (state, { error }) => {
    return {
      ...state,
      userActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.LoadContracts, state => {
    return {
      ...state,
      contractsActionState: ActionStateCreator.onStart(),
    };
  }),

  on(fromActions.LoadContractsSuccess, (state, { contracts }) => {
    const sortedContracts = sortContracts(contracts);
    return {
      ...state,
      contractsActionState: ActionStateCreator.onSuccess(),
      contracts: sortedContracts,
      selectedContract: getPrimaryContract(state.user, sortedContracts),
    };
  }),

  on(fromActions.LoadContractsFailed, (state, { error }) => {
    return {
      ...state,
      contractsActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.SelectContract, (state, { id }) => {
    const selectedContract = state.contracts?.find(contract => contract.id == id);
    const isSameContract = state.selectedContract && state.selectedContract.id == id;

    let newState = {
      ...state,
      selectedContract: selectedContract,
    };

    if (!isSameContract) {
      newState = {
        ...newState,
        selectedContractDocuments: null,
        selectedContractDocumentsActionState: ActionStateCreator.create(),
      };
    }
    return newState;
  }),

  on(fromActions.LoadContractDocuments, state => {
    return {
      ...state,
      selectedContractDocumentsActionState: ActionStateCreator.onStart(),
    };
  }),

  on(fromActions.LoadContractDocumentsSuccess, (state, { documents }) => {
    return {
      ...state,
      selectedContractDocumentsActionState: ActionStateCreator.onSuccess(),
      selectedContractDocuments: documents,
    };
  }),

  on(fromActions.LoadContractDocumentsFailed, (state, { error }) => {
    return {
      ...state,
      selectedContractDocumentsActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.SelectContractDocument, (state, { identifier }) => {
    const document = state.selectedContractDocuments?.find(d => d.identifier == identifier);
    return {
      ...state,
      selectedContractSelectedDocument: document,
    };
  }),
  on(fromActions.LoadMasterDataChangeConfiguration, state => {
    return {
      ...state,
      selectedContractDocumentsActionState: ActionStateCreator.onStart(),
    };
  }),

  on(
    fromActions.LoadMasterDataChangeConfigurationSuccess,
    (state, { masterDataChangeConfiguration }) => {
      return {
        ...state,
        masterDataChangeConfigurationActionState: ActionStateCreator.onSuccess(),
        masterDataChangeConfiguration: masterDataChangeConfiguration,
      };
    }
  ),

  on(fromActions.LoadMasterDataChangeConfigurationError, (state, { error }) => {
    return {
      ...state,
      masterDataChangeConfigurationActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.SetMasterDataChange, (state, { masterDataChange }) => ({
    ...state,
    masterDataChange: {
      ...state.masterDataChange,
      ...masterDataChange,
    },
    masterDataChangeActionState: ActionStateCreator.onSuccess(),
  })),

  on(fromActions.ResetMasterDataChange, state => ({
    ...state,
    masterDataChange: null,
    sendMasterDataChangeRequestResponse: null,
    sendMasterDataChangeTicketResponse: null,
    masterDataChangeActionState: ActionStateCreator.create(),
    sendMasterdataChangeRequestActionState: ActionStateCreator.create(),
    sendMasterdataChangeTicketActionState: ActionStateCreator.create(),
  })),

  on(fromActions.SendMasterDataChangeRequest, state => {
    return {
      ...state,
      sendMasterdataChangeRequestActionState: ActionStateCreator.onStart(),
    };
  }),
  on(fromActions.SendMasterDataChangeRequestSuccess, (state, { success }) => {
    return {
      ...state,
      sendMasterDataChangeRequestResponse: success,
      sendMasterdataChangeRequestActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.SendMasterDataChangeRequestError, (state, { error }) => {
    return {
      ...state,
      sendMasterDataChangeRequestResponse: null,
      sendMasterdataChangeRequestActionState: ActionStateCreator.onError(error),
    };
  }),

  on(fromActions.SendMasterDataChangeTicket, state => {
    return {
      ...state,
      sendMasterdataChangeTicketActionState: ActionStateCreator.onStart(),
    };
  }),

  on(fromActions.SendMasterDataChangeTicketSuccess, (state, { success }) => {
    return {
      ...state,
      sendMasterDataChangeTicketResponse: success,
      sendMasterdataChangeTicketActionState: ActionStateCreator.onSuccess(),
    };
  }),
  on(fromActions.SendMasterDataChangeTicketError, (state, { error }) => {
    return {
      ...state,
      sendMasterDataChangeTicketResponse: false,
      sendMasterdataChangeTicketActionState: ActionStateCreator.onError(error),
    };
  })
);
